// ============================================================================
// Motor de caminho crítico do Acompanhamento NPD.
// Portado do painel original. Puro (sem I/O), determinístico e testável.
//
// Regras (ver docs/ARCHITECTURE.md):
//  - Uma etapa só começa quando TODAS as suas dependências terminam; etapas sem
//    dependência entre si correm em paralelo.
//  - Linha de base: previsão calculada no dia da abertura, com durações padrão.
//  - Previsão atual: usa datas reais quando existem; para etapas não iniciadas,
//    a previsão de início é max(quando poderia começar, hoje) — assim o atraso
//    aparece sozinho a cada dia sem ninguém preencher nada.
//  - Desvio da linha = previsão de conclusão atual − previsão de base (dias).
// ============================================================================

import { addDays, differenceInCalendarDays, max as maxDate, parseISO } from 'date-fns';
import type {
  LineStatus,
  Phase,
  Product,
  ProductStage,
  ProductSummary,
  StageComputed,
  StageStatus,
  StageTemplate,
} from './types';

/** Limiares de status da linha (dias de desvio). Ajustáveis. */
export const THRESHOLDS = {
  /** Acima disto a linha é "Atrasado". */
  atrasado: 7,
  /** De 1 até `atrasado` dias (ou etapa vencida) é "Atenção". */
  atencao: 1,
} as const;

const PHASES: Phase[] = ['PD1', 'PD2', 'PD3', 'PD4', 'PD5'];

function toDate(s: string | null): Date | null {
  return s ? parseISO(s) : null;
}
function fmt(d: Date | null): string | null {
  if (!d) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * Calcula o resumo de uma linha de produto a partir do template e das
 * instâncias de etapa. `today` é injetável para testes determinísticos.
 */
export function computeProductSummary(
  product: Product,
  template: StageTemplate[],
  today: Date = new Date(),
): ProductSummary {
  const tplByN = new Map(template.map((t) => [t.n, t]));
  const stageByN = new Map(product.stages.map((s) => [s.n, s]));
  const opened = toDate(product.openedAt);

  // Ordena por n (deps sempre referenciam n menor → ordem topológica natural).
  const ns = [...template].sort((a, b) => a.n - b.n).map((t) => t.n);

  const baselineFinish = new Map<number, Date | null>();
  const currentFinish = new Map<number, Date | null>();
  const currentStart = new Map<number, Date | null>();
  const earliest = new Map<number, Date | null>();

  const computed: StageComputed[] = [];

  for (const n of ns) {
    const tpl = tplByN.get(n)!;
    const inst: ProductStage =
      stageByN.get(n) ??
      { n, daysPlanned: tpl.defaultDays, status: 'nao_iniciado', startedAt: null, finishedAt: null };

    // ---- Linha de base (durações padrão, a partir da abertura) --------------
    let baseStart: Date | null;
    if (tpl.deps.length === 0) {
      baseStart = opened;
    } else {
      const depFins = tpl.deps.map((d) => baselineFinish.get(d) ?? null).filter(Boolean) as Date[];
      baseStart = depFins.length && opened ? maxDate([opened, ...depFins]) : opened;
    }
    const baseFin = baseStart ? addDays(baseStart, tpl.defaultDays) : null;
    baselineFinish.set(n, baseFin);

    // ---- Início mais cedo possível (com base nas dependências atuais) -------
    let earliestStart: Date | null;
    if (tpl.deps.length === 0) {
      earliestStart = opened;
    } else {
      const depFins = tpl.deps.map((d) => currentFinish.get(d) ?? null);
      if (depFins.some((d) => d === null)) {
        // alguma dependência ainda sem previsão → herda a maior conhecida/abertura
        const known = depFins.filter(Boolean) as Date[];
        earliestStart = known.length ? maxDate(known) : opened;
      } else {
        earliestStart = maxDate(depFins as Date[]);
      }
    }
    earliest.set(n, earliestStart);

    // ---- Previsão atual (usa datas reais quando existem) --------------------
    let predStart: Date | null;
    let predFinish: Date | null;
    const started = toDate(inst.startedAt);
    const finished = toDate(inst.finishedAt);

    if (inst.status === 'concluido') {
      // Concluída: nunca reagenda para o futuro, mesmo sem data preenchida.
      // Usa a data real quando existe; senão o início real/possível ou hoje.
      predStart = started ?? earliestStart;
      predFinish = finished ?? started ?? earliestStart ?? today;
    } else if (inst.status === 'em_andamento' && started) {
      predStart = started;
      predFinish = addDays(started, inst.daysPlanned);
    } else {
      // não iniciada: não pode começar no passado → max(quando poderia, hoje)
      predStart = earliestStart ? maxDate([earliestStart, today]) : null;
      predFinish = predStart ? addDays(predStart, inst.daysPlanned) : null;
    }
    currentStart.set(n, predStart);
    currentFinish.set(n, predFinish);

    // ---- Atraso da etapa ----------------------------------------------------
    let atrasoDias = 0;
    if (inst.status === 'concluido' && started && finished) {
      const dur = differenceInCalendarDays(finished, started);
      atrasoDias = Math.max(0, dur - inst.daysPlanned);
    } else if (inst.status === 'em_andamento' && started) {
      const shouldFinish = addDays(started, inst.daysPlanned);
      atrasoDias = Math.max(0, differenceInCalendarDays(today, shouldFinish));
    } else if (inst.status === 'nao_iniciado' && earliestStart) {
      atrasoDias = Math.max(0, differenceInCalendarDays(today, earliestStart));
    }
    const isLate = atrasoDias > 0 && inst.status !== 'concluido';

    computed.push({
      n,
      phase: tpl.phase,
      name: tpl.name,
      areaKeys: tpl.areaKeys,
      status: inst.status,
      daysPlanned: inst.daysPlanned,
      earliestStart: fmt(earliestStart),
      predictedStart: fmt(predStart),
      predictedFinish: fmt(predFinish),
      baselineFinish: fmt(baseFin),
      atrasoDias,
      isLate,
    });
  }

  // ---- Agregações da linha --------------------------------------------------
  const total = computed.length || 1;
  const concluded = computed.filter((c) => c.status === 'concluido').length;
  const percentConcluido = concluded / total;

  const projFinCur = latest([...currentFinish.values()]);
  const projFinBase = latest([...baselineFinish.values()]);
  const desvioDias =
    projFinCur && projFinBase ? differenceInCalendarDays(projFinCur, projFinBase) : 0;

  const lifecycle = product.lifecycle ?? 'ativo';
  const isConcluido = lifecycle === 'concluido' || concluded === total;

  const firstOpen = computed.find((c) => c.status !== 'concluido');
  const faseAtual: ProductSummary['faseAtual'] = isConcluido
    ? 'Concluído'
    : (firstOpen?.phase ?? '—');

  const anyLate = computed.some((c) => c.isLate);

  // Ciclo de vida (manual) tem precedência sobre a saúde calculada.
  let status: LineStatus;
  if (lifecycle === 'cancelado') status = 'cancelado';
  else if (isConcluido) status = 'concluido';
  else if (lifecycle === 'pausado') status = 'pausado';
  else if (!product.openedAt) status = 'sem_data';
  else if (desvioDias > THRESHOLDS.atrasado) status = 'atrasado';
  else if (desvioDias >= THRESHOLDS.atencao || anyLate) status = 'atencao';
  else status = 'no_prazo';

  // Desvio vs. meta (data-alvo de lançamento): previsão − meta, em dias.
  const target = toDate(product.targetLaunch ?? null);
  const desvioMeta =
    target && projFinCur ? differenceInCalendarDays(projFinCur, target) : null;

  // Próxima etapa acionável: prioriza a que está "em andamento"; senão a
  // primeira "não iniciada" com todas as dependências concluídas (pronta).
  const statusOf = (n: number) => stageByN.get(n)?.status ?? 'nao_iniciado';
  let proximaEtapa: ProductSummary['proximaEtapa'] = null;
  if (lifecycle === 'ativo' && !isConcluido) {
    const wip = computed.find((c) => c.status === 'em_andamento');
    const ready = computed.find(
      (c) =>
        c.status === 'nao_iniciado' &&
        (tplByN.get(c.n)?.deps ?? []).every((d) => statusOf(d) === 'concluido'),
    );
    const pick = wip ?? ready;
    if (pick)
      proximaEtapa = {
        n: pick.n,
        phase: pick.phase,
        name: pick.name,
        areaKeys: pick.areaKeys,
        status: pick.status === 'em_andamento' ? 'em_andamento' : 'nao_iniciado',
      };
  }

  // Status por fase (para a faixa colorida)
  const phaseStatus = {} as Record<Phase, StageStatus | 'atrasada'>;
  for (const ph of PHASES) {
    const inPhase = computed.filter((c) => c.phase === ph);
    if (inPhase.some((c) => c.isLate)) phaseStatus[ph] = 'atrasada';
    else if (inPhase.length && inPhase.every((c) => c.status === 'concluido'))
      phaseStatus[ph] = 'concluido';
    else if (inPhase.some((c) => c.status === 'em_andamento')) phaseStatus[ph] = 'em_andamento';
    else phaseStatus[ph] = 'nao_iniciado';
  }

  return {
    productId: product.id,
    brand: product.brand,
    name: product.name,
    sapCode: product.sapCode ?? null,
    openedAt: product.openedAt,
    targetLaunch: product.targetLaunch ?? null,
    priority: product.priority ?? 'media',
    category: product.category ?? 'inovacao',
    lifecycle,
    projectType: product.projectType ?? 'interno',
    photoUrl: product.photoUrl ?? null,
    faseAtual,
    percentConcluido,
    previsaoConclusao: fmt(projFinCur),
    desvioDias,
    desvioMeta,
    proximaEtapa,
    status,
    phaseStatus,
    stages: computed,
  };
}

function latest(dates: (Date | null)[]): Date | null {
  const valid = dates.filter(Boolean) as Date[];
  return valid.length ? maxDate(valid) : null;
}

// ---------------------------------------------------------------------------
// Agregações do Painel de Atrasos (sobre várias linhas de produto)
// ---------------------------------------------------------------------------
export interface DelayByArea {
  areaKey: string;
  lateStages: number;
  totalDelayDays: number;
}
/** Projeto específico atrasado numa etapa (para expandir no painel). */
export interface DelayItem {
  productId: string;
  brand: string;
  name: string;
  atrasoDias: number;
}
export interface DelayByStage {
  n: number;
  name: string;
  phase: Phase;
  lateProducts: number;
  totalDelayDays: number;
  items: DelayItem[]; // quais projetos estão atrasados nesta etapa
}

/**
 * Agrega os atrasos das linhas. "byArea" (atraso por responsável) é sempre a
 * visão completa do time. "byStage" (atraso por etapa) pode ser restringido às
 * áreas do usuário via `stageAreaFilter` — assim cada pessoa vê apenas as etapas
 * que competem a ela; admin/gestor passam `undefined` e veem tudo.
 */
export function aggregateDelays(
  summaries: ProductSummary[],
  stageAreaFilter?: string[],
): {
  byArea: DelayByArea[];
  byStage: DelayByStage[];
} {
  const areaMap = new Map<string, DelayByArea>();
  const stageMap = new Map<number, DelayByStage>();

  for (const s of summaries) {
    for (const st of s.stages) {
      if (!st.isLate) continue;
      // por área (visão de time — não filtra)
      for (const ak of st.areaKeys) {
        const cur = areaMap.get(ak) ?? { areaKey: ak, lateStages: 0, totalDelayDays: 0 };
        cur.lateStages += 1;
        cur.totalDelayDays += st.atrasoDias;
        areaMap.set(ak, cur);
      }
      // por etapa (respeita o filtro de áreas do usuário, quando informado)
      if (stageAreaFilter && !st.areaKeys.some((k) => stageAreaFilter.includes(k))) continue;
      const cur = stageMap.get(st.n) ?? {
        n: st.n,
        name: st.name,
        phase: st.phase,
        lateProducts: 0,
        totalDelayDays: 0,
        items: [],
      };
      cur.lateProducts += 1;
      cur.totalDelayDays += st.atrasoDias;
      cur.items.push({
        productId: s.productId,
        brand: s.brand,
        name: s.name,
        atrasoDias: st.atrasoDias,
      });
      stageMap.set(st.n, cur);
    }
  }

  for (const st of stageMap.values()) st.items.sort((a, b) => b.atrasoDias - a.atrasoDias);

  return {
    byArea: [...areaMap.values()].sort((a, b) => b.totalDelayDays - a.totalDelayDays),
    byStage: [...stageMap.values()].sort((a, b) => b.totalDelayDays - a.totalDelayDays),
  };
}

export const KPIS = (summaries: ProductSummary[]) => ({
  atrasado: summaries.filter((s) => s.status === 'atrasado').length,
  atencao: summaries.filter((s) => s.status === 'atencao').length,
  noPrazo: summaries.filter((s) => s.status === 'no_prazo').length,
  concluido: summaries.filter((s) => s.status === 'concluido').length,
  semData: summaries.filter((s) => s.status === 'sem_data').length,
  total: summaries.length,
});
